import React, { useState, useEffect } from 'react';
import type { SvgIcon } from '../types';

interface SvgEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (newIcon: SvgIcon) => void;
    iconName: string;
    initialIcon: SvgIcon;
}

const SvgEditModal: React.FC<SvgEditModalProps> = ({ isOpen, onClose, onSave, iconName, initialIcon }) => {
    const [svgCode, setSvgCode] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            // Reconstruct a full SVG tag for easier editing
            setSvgCode(`<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="${initialIcon.viewBox}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${initialIcon.svg}</svg>`);
            setError('');
        }
    }, [isOpen, initialIcon]);

    if (!isOpen) return null;

    const handleSave = () => {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(svgCode, "image/svg+xml");
            const svgElement = doc.querySelector('svg');

            if (!svgElement) {
                throw new Error("No valid <svg> tag found.");
            }

            const viewBox = svgElement.getAttribute('viewBox') || '0 0 24 24';
            
            // Remove fill and stroke attributes from all child elements
            svgElement.querySelectorAll('*').forEach(el => {
                el.removeAttribute('fill');
                el.removeAttribute('stroke');
            });
            
            const innerSvg = svgElement.innerHTML;

            onSave({ svg: innerSvg, viewBox });
            setError('');

        } catch (e: any) {
            setError(`Invalid SVG code: ${e.message}`);
            console.error(e);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center" aria-modal="true" role="dialog">
            <div className="bg-white rounded-lg shadow-xl m-4 sm:max-w-lg sm:w-full" role="document">
                <div className="p-6">
                    <div className="sm:flex sm:items-start">
                        <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                            <h3 className="text-lg leading-6 font-medium text-gray-900 capitalize" id="modal-title">
                                Edit Icon: {iconName}
                            </h3>
                            <div className="mt-4">
                                <p className="text-sm text-gray-500 mb-2">
                                    Paste your full {'<svg>'} code below. The system will automatically extract the content, get the correct `viewBox`, and remove colors to match the theme.
                                </p>
                                <textarea
                                    value={svgCode}
                                    onChange={(e) => setSvgCode(e.target.value)}
                                    rows={8}
                                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-gray-500 font-mono text-xs ${error ? 'border-red-500' : 'border-gray-300'}`}
                                    placeholder={'<svg xmlns="http://www.w3.org/2000/svg" ...>...</svg>'}
                                />
                                {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse rounded-b-lg">
                    <button
                        type="button"
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gray-800 text-base font-medium text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:ml-3 sm:w-auto sm:text-sm"
                        onClick={handleSave}
                    >
                        Save Icon
                    </button>
                    <button
                        type="button"
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SvgEditModal;